import React, { useState } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import comunicacaoService from '../../services/comunicacaoService';
import BarraFormatacao from '../common/BarraFormatacao';

interface ModalCriarTopicoProps {
  canalId: string;
  canalNome?: string;
  onClose: () => void;
}

const ModalCriarTopico: React.FC<ModalCriarTopicoProps> = ({ canalId, canalNome, onClose }) => {
  const { criarTopico } = useComunicacao();
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [salvando, setSalvando] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const novosArquivos = Array.from(e.target.files);
      e.target.value = '';
      setArquivos(prev => [...prev, ...novosArquivos]);
    }
  };

  const removerArquivo = (index: number) => {
    setArquivos(prev => prev.filter((_, i) => i !== index));
  };

  const formatarTamanho = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!titulo.trim() || !mensagem.trim()) {
      return;
    }

    try {
      setSalvando(true);
      const topico = await criarTopico({
        titulo: titulo.trim(),
        mensagem: mensagem.trim(),
        canalId
      });

      // Upload de anexos
      if (arquivos.length > 0 && topico.id) {
        for (const arquivo of arquivos) {
          await comunicacaoService.uploadAnexoTopico(topico.id, arquivo);
        }
      }

      onClose();
    } catch (error) {
      console.error('Erro ao criar tópico:', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-800">Criar Novo Tópico</h2>
          {canalNome && (
            <p className="text-sm text-gray-500 mt-1">
              Canal: <span className="font-medium text-bravo-primary"># {canalNome}</span>
            </p>
          )}
        </div>

        <form id="form-criar-topico" onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título *
            </label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Digite o título do tópico..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem *
            </label>
            <BarraFormatacao
              valor={mensagem}
              onChange={setMensagem}
              placeholder="Escreva sua mensagem..."
              minHeight={200}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Anexos (opcional)
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              multiple
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Máximo 50MB por arquivo</p>
            
            {arquivos.length > 0 && (
              <div className="mt-3 space-y-2">
                {arquivos.map((arquivo, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-700">{arquivo.name}</p>
                        <p className="text-xs text-gray-500">{formatarTamanho(arquivo.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removerArquivo(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            disabled={salvando}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="form-criar-topico"
            disabled={!titulo.trim() || !mensagem.trim() || salvando}
            className="px-4 py-2 bg-bravo-primary hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {salvando ? 'Criando...' : 'Criar Tópico'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalCriarTopico;
